/*
 * Infinite scroll for the collection and search product grids.
 *
 * Progressive enhancement: the server-rendered numbered pagination stays in the
 * DOM and is only hidden once this script takes over. If JavaScript is off, or a
 * fetch fails, the visitor keeps the normal numbered pagination as a fallback.
 *
 * Works on both layouts because it targets the list by class (`ul.product-grid`):
 * on collection pages the id `product-grid` is the <ul>, on search it is a parent
 * <div>, so the class is the reliable hook. The next-page URL is read from
 * `.pagination-wrapper[data-next-url]`, which pagination.liquid renders.
 */
(function () {
  const CONTAINER_ID = 'ProductGridContainer';
  // Bilingual status text, keyed off the storefront locale (<html lang>).
  const LANG = (document.documentElement.lang || 'fr').toLowerCase();
  const LOADING_TEXT = LANG.indexOf('en') === 0 ? 'Loading products…' : 'Chargement des produits…';

  class InfiniteScroll {
    constructor(container) {
      this.container = container;
      this.list = container.querySelector('ul.product-grid');
      this.paginationWrapper = container.querySelector('.pagination-wrapper');
      this.nextUrl = this.paginationWrapper
        ? this.paginationWrapper.getAttribute('data-next-url')
        : null;
      this.loading = false;

      // Nothing to do when the list is missing or there is no further page.
      if (!this.list || !this.nextUrl) return;

      this.hideNumberedNav();
      this.buildSentinel();

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) this.loadNext();
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this.sentinel);
    }

    hideNumberedNav() {
      const nav = this.paginationWrapper.querySelector('.pagination');
      if (nav) nav.hidden = true;
    }

    showNumberedNav() {
      const nav = this.paginationWrapper.querySelector('.pagination');
      if (nav) nav.hidden = false;
    }

    buildSentinel() {
      this.sentinel = document.createElement('div');
      this.sentinel.className = 'infinite-scroll__sentinel';
      this.status = document.createElement('p');
      this.status.className = 'infinite-scroll__status';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.style.textAlign = 'center';
      this.status.style.margin = '0';
      this.status.style.padding = '1.5rem 0';
      this.status.style.minHeight = '1em';
      this.sentinel.appendChild(this.status);
      // Insert just before the (now hidden) pagination so it sits at the bottom.
      this.paginationWrapper.parentNode.insertBefore(this.sentinel, this.paginationWrapper);
    }

    async loadNext() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      this.status.textContent = LOADING_TEXT;

      try {
        const response = await fetch(this.nextUrl);
        if (!response.ok) throw new Error('HTTP ' + response.status);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = doc.getElementById(CONTAINER_ID);
        const newList = source && source.querySelector('ul.product-grid');

        if (newList) {
          const fragment = document.createDocumentFragment();
          newList.querySelectorAll(':scope > li').forEach((li) => {
            fragment.appendChild(document.importNode(li, true));
          });
          this.list.appendChild(fragment);
        }

        const newWrapper = source && source.querySelector('.pagination-wrapper');
        this.nextUrl = newWrapper ? newWrapper.getAttribute('data-next-url') : null;
        this.status.textContent = '';

        if (!this.nextUrl) this.finish();
      } catch (error) {
        // Give the visitor the real pagination back so they can page manually.
        this.status.textContent = '';
        this.showNumberedNav();
        this.finish();
      } finally {
        this.loading = false;
      }
    }

    // Reached the last page (or errored): stop observing and remove the marker.
    finish() {
      this.destroy();
    }

    destroy() {
      if (this.observer) this.observer.disconnect();
      if (this.sentinel && this.sentinel.parentNode) {
        this.sentinel.parentNode.removeChild(this.sentinel);
      }
    }
  }

  let instance = null;

  function setup() {
    const container = document.getElementById(CONTAINER_ID);
    if (!container) return;
    if (instance) instance.destroy();
    instance = new InfiniteScroll(container);
  }

  function boot() {
    const container = document.getElementById(CONTAINER_ID);
    if (!container) return;

    setup();

    // Dawn's facets/sorting replace the grid's innerHTML on filter changes.
    // Re-initialise when the container's direct children are swapped out.
    new MutationObserver(() => setup()).observe(container, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
